/**
 * TwoFARecoveryScreen — login-time recovery for users locked out of
 * their authenticator. Reachable from the /2fa/verify page via a
 * "lost access?" link. Carries the partial_token via router state.
 */

import { Anchor, Button, Container, Paper, Stack, Text, TextInput, Title } from "@mantine/core";
import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";

import { ApiError, api, auth, type MeResponse } from "../lib/api";

export function TwoFARecoveryScreen({
  onLoggedIn,
}: {
  onLoggedIn: (me: MeResponse) => void;
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const partialToken = (location.state as { partialToken?: string } | null)?.partialToken;

  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!partialToken) {
    return <Navigate to="/login" replace />;
  }

  const handle = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const tokens = await api.twoFARecoveryRedeem(partialToken, code);
      auth.setTokens(tokens);
      const me = await api.me();
      onLoggedIn(me);
      navigate("/2fa/setup", {
        replace: true,
        state: { fromRecovery: true },
      });
    } catch (err) {
      if (err instanceof ApiError && err.detail === "invalid_recovery_code") {
        setError("קוד שחזור לא תקין.");
      } else if (err instanceof ApiError && err.detail === "no_recovery_codes") {
        setError("אין קודי שחזור פעילים בחשבון. פנה לתמיכה.");
      } else {
        setError("משהו השתבש. נסה שוב.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Container size="xs" py="xl">
      <Paper p="lg" radius="md" withBorder>
        <Stack>
          <Title order={2}>שחזור גישה</Title>
          <Text c="dimmed" size="sm">
            הזן אחד מקודי השחזור שיצרת בעת הפעלת 2FA. הקוד הוא חד־פעמי
            ויצרך מחדש לאחר השימוש.
          </Text>
          <form onSubmit={handle}>
            <Stack>
              <TextInput
                label="קוד שחזור"
                value={code}
                onChange={(e) => setCode(e.currentTarget.value)}
                dir="ltr"
                placeholder="XXXX-XXXX-XXXX"
                required
                autoFocus
              />
              {error && (
                <Text c="red" size="sm">
                  {error}
                </Text>
              )}
              <Button type="submit" loading={busy} disabled={!code.trim()} fullWidth>
                שחזר גישה
              </Button>
              <Anchor component={Link} to="/2fa/verify" state={{ partialToken }} size="sm">
                חזרה לאימות 2FA רגיל
              </Anchor>
            </Stack>
          </form>
        </Stack>
      </Paper>
    </Container>
  );
}
